import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from 'components';
import { PathName } from 'appConstants';
import {
  eclipseDriveCar1Icon,
  eclipseDriveCar2Icon,
  eclipseDriveCar3Icon,
  eclipseDriveRolexIcon,
  ekiHugIcon,
  eclipseDriveTextBackIcon,
} from 'assets/images';
import { useSelector } from 'react-redux';
import { profileSelectors } from 'store/profile/selectors';
import styles from './styles.module.scss';

interface IFinalEventSectionProps {
  isAuthenticated: boolean;
}

const otherPrizes = [
  { placeKey: 'secondPlace', amountKey: 'secondAmount' },
  { placeKey: 'thirdPlace', amountKey: 'thirdAmount' },
  { placeKey: 'otherPlaces', amountKey: 'otherAmount' },
] as const;

const infoKeys = ['info1', 'info2'] as const;

const carImages = [
  { src: eclipseDriveCar1Icon, alt: 'ZEEKR 001 Car 1' },
  { src: eclipseDriveCar2Icon, alt: 'ZEEKR 001 Car 2' },
  { src: eclipseDriveCar3Icon, alt: 'ZEEKR 001 Car 3' },
];

export const PrizePoolSection = ({ isAuthenticated }: IFinalEventSectionProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('eclipseDrive');
  const { referralCode } = useSelector(profileSelectors.getState);
  
  return (
    <div className={styles.section}>
      <div className={styles.section_content}>
        <div className={styles.header}>
          <h2 className={styles.section_title}>{t('prizePool.title')}</h2>
          <p className={styles.section_subtitle}>{t('prizePool.subtitle')}</p>
        </div>

        <h3 className={styles.cash_prizes_title}>{t('prizePool.cashPrizesTitle')}</h3>
        
        <div className={styles.prizes_wrapper}>
          <div className={styles.center_content}>
            <div className={styles.grand_prize_bar}>
              <div
                className={styles.fund_badge}
                style={{ backgroundImage: `url(${eclipseDriveTextBackIcon})` }}
              >
                <span className={styles.fund_label}>{t('prizePool.totalLabel')}</span>
                <span className={styles.fund_label}>{t('prizePool.prizeFundLabel')}</span>
                <span className={styles.fund_amount}>{t('prizePool.totalAmount')}</span>
              </div>
              <div className={styles.grand_prize_content}>
                <span className={styles.grand_label}>{t('prizePool.grandPrize')}</span>
                <span className={styles.grand_amount}>{t('prizePool.grandAmount')}</span>
              </div>
              <img
                src={ekiHugIcon}
                alt="Eki Hug"
                className={styles.eki_hug_icon}
              />
            </div>

            <div className={styles.other_prizes}>
              {otherPrizes.map(({ placeKey, amountKey }) => (
                <div
                  key={placeKey}
                  className={styles.prize_card}
                >
                  <div className={styles.prize_place}>{t(`prizePool.${placeKey}`)}</div>
                  <div className={styles.prize_value}>{t(`prizePool.${amountKey}`)}</div>
                </div>
              ))}
            </div>

            <div className={styles.prize_info}>
              {infoKeys.map((key) => (
                <p key={key}>
                  <span className={styles.bullet}>•</span>
                  <span className={styles.text_content}>
                    {t(`prizePool.${key}`)}
                  </span>
                </p>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.material_prizes}>
          <h3 className={styles.prize_category_title}>{t('prizePool.materialPrizesTitle')}</h3>
          
          <div className={styles.material_items}>
            <div className={styles.cars_group}>
              <div className={styles.cars_images}>
                {carImages.map(({ src, alt }) => (
                  <img
                    key={alt}
                    src={src}
                    alt={alt}
                    className={styles.car_image}
                  />
                ))}
              </div>
              <p className={styles.material_label}>{t('prizePool.zeekrCars')}</p>
            </div>
            
            <div className={styles.watch_item}>
              <img
                src={eclipseDriveRolexIcon}
                alt="ROLEX DAYTONA"
                className={styles.watch_image}
              />
              <div className={styles.material_label}>
                <div>{t('prizePool.rolexLine1')}</div>
                <div>{t('prizePool.rolexLine2')}</div>
              </div>
            </div>
          </div>
        </div>

        <p className={styles.bottom_note}>
          {t('prizePool.bottomNote')}
        </p>
        
        {!isAuthenticated && (
          <div className={styles.cta_wrapper}>
            <Button
              type="button"
              className={styles.cta_button}
              onClick={() => navigate(`${PathName.Registration}/?ref=${referralCode}`)}
            >
              {t('prizePool.cta')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
